import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";

const PaymentMethodNewEdit = () => {
  usePageTitle("Edit Payment Method");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const {
    overlayData,
    setOverlayName,
    showSnackbar,
    handleErrorDialog,
    openDialog,
    closeDialog,
  } = useStateContext();

  const isEditMode = !!overlayData?.paymentMethodId;
  const allPaymentMethods = useMemo(
    () => overlayData?.allPaymentMethods || [],
    [overlayData?.allPaymentMethods],
  );
  const usageCount = Number(overlayData?.usageCount || 0);

  const [name, setName] = useState(overlayData?.name || "");
  const [initialName] = useState(overlayData?.name || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const closeOverlay = () => setOverlayName("");

  const isDuplicateName = useMemo(() => {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return false;

    return allPaymentMethods.some(
      (method) =>
        (method.Name || "").toLowerCase() === trimmed &&
        method.id !== overlayData?.paymentMethodId,
    );
  }, [name, allPaymentMethods, overlayData?.paymentMethodId]);

  const emitSettingsSaved = (recordId, changeAction) => {
    if (!socket || !currentUser?.id) return;

    socket.emit("settings:saved", {
      settingsType: "PaymentMethod",
      recordId,
      changeAction,
      user: {
        id: currentUser.id,
        name: currentUser.Name,
        imageThumbnail: currentUser.ImageThumbnail,
      },
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showSnackbar("Payment method name is required", theme, "error");
      return;
    }

    if (isDuplicateName) {
      showSnackbar("This payment method already exists", theme, "error");
      return;
    }

    const nameChanged = name.trim() !== initialName.trim();
    if (!nameChanged) {
      showSnackbar("No changes to save", theme, "info");
      return;
    }

    setSaving(true);

    try {
      if (isEditMode) {
        await api.put(`/clients/settings/paymentMethods/${overlayData.paymentMethodId}`, {
          Name: name.trim(),
        });
        showSnackbar("Payment method updated successfully", theme, "success");
        emitSettingsSaved(overlayData.paymentMethodId, "Updated");
      } else {
        const response = await api.post("/clients/settings/paymentMethods", {
          Name: name.trim(),
        });
        showSnackbar("Payment method created successfully", theme, "success");
        emitSettingsSaved(response.data?.id, "Created");
      }

      overlayData?.onSaved?.();
      closeOverlay();
    } catch (err) {
      console.error(err);
      handleErrorDialog(
        err?.response?.data?.error || "Error saving payment method",
        theme,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!isEditMode) return;

    if (usageCount > 0) {
      showSnackbar(
        `Cannot delete. In use by ${usageCount} client${usageCount > 1 ? "s" : ""}`,
        theme,
        "error",
      );
      return;
    }

    openDialog({
      theme,
      title: "Delete Payment Method",
      message: `Are you sure you want to delete "${name}"?`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        setDeleting(true);
        try {
          await api.delete(`/clients/settings/paymentMethods/${overlayData.paymentMethodId}`);
          closeDialog();
          showSnackbar("Payment method deleted", theme, "success");
          emitSettingsSaved(overlayData.paymentMethodId, "Deleted");
          overlayData?.onSaved?.();
          closeOverlay();
        } catch (err) {
          console.error(err);
          closeDialog();
          handleErrorDialog(
            err?.response?.data?.error || "Error deleting payment method",
            theme,
          );
        } finally {
          setDeleting(false);
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const pageTitle = isEditMode ? "Edit Payment Method" : "New Payment Method";

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onClick={closeOverlay}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "800px",
          maxHeight: "80%",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: "0.5rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "60%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton color="primary" size="large" onClick={closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {pageTitle}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              width: "40%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            {isEditMode && (
              isMobile ? (
                <IconButton
                  color="error"
                  onClick={handleDelete}
                  disabled={deleting || usageCount > 0}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.error.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DeleteRoundedIcon />
                </IconButton>
              ) : (
                <Button
                  loading={deleting}
                  color="error"
                  variant="outlined"
                  size="large"
                  endIcon={<DeleteRoundedIcon />}
                  onClick={handleDelete}
                  disabled={deleting || usageCount > 0}
                  sx={{ borderRadius: "12px" }}
                >
                  Delete
                </Button>
              )
            )}

            {isMobile ? (
              <Tooltip title="Discard" placement="top">
                <IconButton
                  color="primary"
                  onClick={closeOverlay}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DisabledByDefaultRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                size="large"
                onClick={closeOverlay}
                sx={{ borderRadius: "12px" }}
              >
                Discard
              </Button>
            )}

            <Tooltip
              title={isMobile && (saving || isDuplicateName) ? "Please fix errors before saving" : ""}
              placement="top"
            >
              <span>
                {isMobile ? (
                  <IconButton
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || isDuplicateName}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.default,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                ) : (
                  <Button
                    loading={saving}
                    color="primary"
                    variant="contained"
                    size="large"
                    endIcon={<SaveRoundedIcon />}
                    onClick={handleSave}
                    disabled={saving || isDuplicateName}
                    sx={{ borderRadius: "12px" }}
                  >
                    {isEditMode ? "Update" : "Create"}
                  </Button>
                )}
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          {isEditMode && usageCount > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This payment method is used by {usageCount} client{usageCount > 1 ? "s" : ""} and cannot be deleted.
            </Alert>
          )}

          <TextField
            fullWidth
            size="small"
            label="Payment Method Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g., ACH"
            error={isDuplicateName}
            helperText={isDuplicateName ? "This name already exists" : ""}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentMethodNewEdit;
